import {Fragment} from 'react';
import {Link} from 'react-router-dom';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import {SiteHeader} from '@common/ui/navigation/site-header';
import {Footer} from '@common/ui/footer/footer';

const featureRows = [
  {
    title: (
      <>
        <span className="text-[#d8562a]">File sharing </span>in a few clicks
      </>
    ),
    body: `Generate shareable links or QR codes for any file or folder. Send it directly through your drive or the dedicated "Send" page. Recipients open it in a browser - no app required.`,
    reverse: false,
    imageSrc: '/images/Page_Share/File_Sharing_in_a_Few_Clicks.png',
    imageAlt: 'File sharing in a few clicks',
  },
  {
    title: (
      <>
        <span className="text-[#d8562a]">Password-protected</span> sharing
      </>
    ),
    body: `Add a password to any share. We hash it with Argon2id the modern standard. So even we can't see what you set.`,
    reverse: true,
    imageSrc: '/images/Page_Share/Password-Protected_Sharing.png',
    imageAlt: 'Password-protected sharing',
  },
  {
    title: (
      <>
        <span className="text-[#d8562a]">Expiring </span>links &amp; QR codes
      </>
    ),
    body: `Set links to expire after a number of hours, days, months, or years. Once they expire, they're gone. No lingering access, no surprises six months later.`,
    reverse: false,
    imageSrc: '/images/Page_Share/Expiring_Links_%26%20QR_Codes.png',
    imageAlt: 'Expiring links and QR codes',
  },
  {
    title: (
      <>
        <span className="text-[#d8562a]">Pay-as-you-go</span> overage
      </>
    ),
    body: `Each tier comes with a generous monthly transfer allowance, double the storage limit. Hit the cap? No problem. Pay-as-you-go one time or automatically so your shares never break.`,
    reverse: true,
    imageSrc: '/images/Page_Share/Pay-as-You-Go_Overage.png',
    imageAlt: 'Pay-as-you-go overage',
  },
];

const shareHeroImage = {
  src: '/images/Page_Share/Simple_Sharing,_Keep_Control.png',
  alt: 'Simple sharing, keep control',
} as const;

const IMAGE_FRAME_CLASS =
  'mx-auto h-[200px] w-[92%] object-contain object-center lg:h-[430px] lg:w-[90%]';

const IMAGE_WRAP_CLASS = 'w-full';

export function SharePage() {
  return (
    <Fragment>
      <DefaultMetaTags />
      <main className="min-h-screen bg-[#f5f5f7] text-[#1a1a1a] dark:bg-[#14161a] dark:text-[#f3f4f6]">
        <SiteHeader active="features" />
        <div className="relative mx-auto w-full max-w-[1895px] px-[100px] pb-[24px] lg:pb-[25px] max-[767px]:px-[100px]">
          <section className="mx-auto mt-[56px] grid w-full max-w-[1565px] grid-cols-1 gap-[28px] lg:mt-[170px] lg:gap-[34px] lg:grid-cols-[minmax(0,1fr)_638px] lg:items-start lg:items-start min-[1024px]:max-[1386px]:grid-cols-[minmax(0,1fr)_400px]">
            <div>
              <h1 className="max-w-[649px] font-['Inter',sans-serif] text-[34px] font-bold leading-[1.1] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[38px] lg:font-['JetBrains_Mono',monospace] lg:text-[60px] lg:leading-[1.08]">
                Simple sharing.
                <br />
                Keep control.
              </h1>
              <p className="mt-[18px] max-w-[649px] text-[18px] font-bold leading-[1.36] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] lg:mt-[30px] lg:text-[25px] lg:leading-[1.32]">
                <span className="italic text-[#d8562a]">Share</span> files with
                anyone, even people who don&apos;t have a CloudTransfer account.
                <br />
                <br />
                <span className="italic text-[#d8562a]">Set</span> expirations.
                <br />
                <span className="italic text-[#d8562a]">require</span> passwords.
                <br />
                <span className="italic text-[#d8562a]">revoke</span> access whenever
                you want.
              </p>
            </div>
            <div className="flex flex-col items-center gap-[18px] lg:gap-[28px]">
              <div className={IMAGE_WRAP_CLASS}>
                <img
                  src={shareHeroImage.src}
                  alt={shareHeroImage.alt}
                  className={IMAGE_FRAME_CLASS}
                />
              </div>
              <Link
                to="/register"
                className="inline-flex h-[60px] w-[141px] items-center justify-center rounded-[24px] bg-[#2b7e20] text-[17px] font-medium text-white transition-colors hover:bg-[#3c7d44]"
              >
                Try for free
              </Link>
            </div>
          </section>

          <div className="mx-auto mt-[72px] grid w-full max-w-[1565px] grid-cols-1 gap-y-[80px] lg:mt-[110px] lg:gap-y-[132px]">
            {featureRows.map(section => (
              <section key={section.body}>
                <h2
                  className={`font-['Inter',sans-serif] text-[32px] font-bold leading-[1.15] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[36px] lg:text-[60px] lg:leading-[1.1] ${
                    section.reverse ? 'text-left lg:text-right' : 'text-left'
                  }`}
                >
                  {section.title}
                </h2>
                <div className="mt-[20px] grid grid-cols-1 items-center gap-[24px] lg:mt-[30px] lg:gap-[36px] lg:grid-cols-2">
                  <div
                    className={`${
                      section.reverse ? 'lg:order-2' : 'lg:order-1'
                    } ${IMAGE_WRAP_CLASS}`}
                  >
                    <img
                      src={section.imageSrc}
                      alt={section.imageAlt}
                      className={IMAGE_FRAME_CLASS}
                    />
                  </div>
                  <p
                    className={`${
                      section.reverse ? 'lg:order-1' : 'lg:order-2'
                    } max-w-[631px] text-[18px] font-bold leading-[1.38] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] lg:text-[25px] lg:leading-[1.35]`}
                  >
                    {section.body}
                  </p>
                </div>
              </section>
            ))}
          </div>

          <section className="mx-auto mt-[84px] w-full max-w-[1565px] text-center lg:mt-[130px]">
            <h2 className="font-['Inter',sans-serif] text-[36px] font-bold leading-[1.2] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[42px] lg:text-[60px] lg:leading-[1.16]">
              Want to give it a try?
            </h2>
            <p className="mx-auto mt-[14px] max-w-[629px] text-[18px] font-normal leading-[1.4] text-[#1a1a1a] dark:text-[#e4e8ef] sm:text-[22px] lg:mt-[20px] lg:text-[30px] lg:leading-[1.35]">
              Whether you choose a free account, paid or just passing by, we have
              something for you.
            </p>
            <Link
              to="/register"
              className="mt-[22px] inline-flex h-[64px] items-center justify-center rounded-[30px] bg-[#2b7e20] px-[30px] text-[21px] font-medium text-white transition-colors hover:bg-[#3c7d44] lg:mt-[32px] lg:h-[108px] lg:rounded-[43px] lg:px-[44px] lg:text-[30px]"
            >
              Try for free
            </Link>
          </section>

          <Footer className="mt-[44px] lg:mt-[120px] lg:min-h-[190px]" />
        </div>
      </main>
    </Fragment>
  );
}
